/**
 * Widget de Previsão do Tempo — Goiânia, GO
 * Fonte: Open-Meteo API (gratuita, sem chave)
 * Sem cantos arredondados.
 */

interface WeatherResponse {
  current: {
    temperature_2m: number
    relative_humidity_2m: number
    wind_speed_10m: number
    weather_code: number
    apparent_temperature: number
  }
  daily: {
    time: string[]
    temperature_2m_max: number[]
    temperature_2m_min: number[]
    weather_code: number[]
  }
}

interface WeatherCondition {
  label: string
  icon: string
}

const WMO: Record<number, WeatherCondition> = {
  0: { label: 'Céu limpo', icon: '☀️' },
  1: { label: 'Mainly clear', icon: '🌤️' },
  2: { label: 'Parcialmente nublado', icon: '⛅' },
  3: { label: 'Nublado', icon: '☁️' },
  45: { label: 'Névoa', icon: '🌫️' },
  48: { label: 'Névoa com geada', icon: '🌫️' },
  51: { label: 'Garoa leve', icon: '🌦️' },
  53: { label: 'Garoa moderada', icon: '🌦️' },
  55: { label: 'Garoa forte', icon: '🌦️' },
  61: { label: 'Chuva leve', icon: '🌧️' },
  63: { label: 'Chuva moderada', icon: '🌧️' },
  65: { label: 'Chuva forte', icon: '🌧️' },
  71: { label: 'Neve leve', icon: '🌨️' },
  80: { label: 'Pancadas leves', icon: '🌦️' },
  81: { label: 'Pancadas moderadas', icon: '🌧️' },
  82: { label: 'Pancadas fortes', icon: '⛈️' },
  95: { label: 'Tempestade', icon: '⛈️' },
  99: { label: 'Tempestade c/ granizo', icon: '⛈️' },
}

const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

function getCondition(code: number): WeatherCondition {
  if (WMO[code]) return WMO[code]
  const base = Math.floor(code / 10) * 10
  return WMO[base] ?? { label: 'Variável', icon: '🌡️' }
}

async function fetchWeather(): Promise<WeatherResponse> {
  const params = new URLSearchParams({
    latitude: '-16.6869',
    longitude: '-49.2648',
    current:
      'temperature_2m,relative_humidity_2m,apparent_temperature,wind_speed_10m,weather_code',
    daily: 'temperature_2m_max,temperature_2m_min,weather_code',
    timezone: 'America/Sao_Paulo',
    forecast_days: '5',
    wind_speed_unit: 'kmh',
  })

  const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`)
  if (!res.ok) throw new Error('Falha ao buscar clima')
  return res.json()
}

function renderWeather(data: WeatherResponse, container: HTMLElement): void {
  const { current, daily } = data
  const cond = getCondition(current.weather_code)

  const forecast = daily.time
    .slice(1, 5)
    .map((dateStr, i) => {
      const d = new Date(dateStr + 'T12:00:00')
      const dayCond = getCondition(daily.weather_code[i + 1])
      return `
        <div class="flex flex-col items-center gap-0.5 flex-1">
          <span class="text-[10px] text-blue-300 font-medium">${DAYS[d.getDay()]}</span>
          <span class="text-xl leading-none">${dayCond.icon}</span>
          <span class="text-xs font-bold text-white">${Math.round(daily.temperature_2m_max[i + 1])}°</span>
          <span class="text-[10px] text-blue-300">${Math.round(daily.temperature_2m_min[i + 1])}°</span>
        </div>`
    })
    .join('')

  container.innerHTML = `
    <div class="bg-gradient-to-br from-[#0a1f3c] via-[#0f2c4a] to-[#1a4a7a] p-4 text-white select-none h-full flex flex-col">

      <div class="flex items-start justify-between mb-3">
        <div>
          <p class="text-[10px] uppercase tracking-widest text-blue-300 font-semibold">Goiânia · GO</p>
          <p class="text-[11px] text-blue-200/70 mt-0.5">${cond.label}</p>
        </div>
        <span class="text-4xl leading-none">${cond.icon}</span>
      </div>

      <div class="flex items-end gap-1 mb-0.5">
        <span class="text-6xl font-bold leading-none tracking-tight">${Math.round(current.temperature_2m)}</span>
        <span class="text-xl text-blue-300 mb-1">°C</span>
      </div>
      <p class="text-[11px] text-blue-200/70 mb-4">
        Sensação <strong class="text-blue-200">${Math.round(current.apparent_temperature)}°C</strong>
      </p>

      <div class="flex gap-4 border-t border-white/10 pt-3 mb-4">
        <div class="flex items-center gap-1.5">
          <svg class="w-3.5 h-3.5 text-blue-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 15a4 4 0 0 0 4 4h9a5 5 0 0 0 1.02-9.916 7 7 0 0 0-13.78 2.096A4 4 0 0 0 3 15z"/>
          </svg>
          <span class="text-xs text-blue-100">${current.relative_humidity_2m}<span class="text-blue-300">%</span></span>
        </div>
        <div class="flex items-center gap-1.5">
          <svg class="w-3.5 h-3.5 text-blue-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2M9.6 4.6A2 2 0 1 1 11 8H2m10.6 11.4A2 2 0 1 0 14 16H2"/>
          </svg>
          <span class="text-xs text-blue-100">${Math.round(current.wind_speed_10m)}<span class="text-blue-300"> km/h</span></span>
        </div>
      </div>

      <div class="border-t border-white/10 pt-3 mt-auto">
        <div class="flex justify-between gap-1">${forecast}</div>
      </div>

      <p class="text-[9px] text-blue-300/40 text-right mt-2">Open-Meteo</p>
    </div>`
}

function renderError(container: HTMLElement): void {
  container.innerHTML = `
    <div class="bg-gradient-to-br from-[#0a1f3c] to-[#1a4a7a] p-4 text-white h-full flex flex-col items-center justify-center gap-2 min-h-[200px]">
      <span class="text-3xl">⛅</span>
      <span class="text-xs text-blue-200 text-center leading-relaxed">
        Não foi possível<br>carregar o clima
      </span>
    </div>`
}

function initWeather(): void {
  const container = document.getElementById('weather-widget')
  if (!container) return

  fetchWeather()
    .then((data) => renderWeather(data, container))
    .catch(() => renderError(container))
}

document.addEventListener('DOMContentLoaded', initWeather)
